import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { StorageService } from './storage.service';
import { LoggingMessageService } from './logging-message.service';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { catchError, delay, map, Observable, of, retry, take } from 'rxjs';
import { DataResult } from '../models/data-result.model';
import { FileResult } from '../models/file-result.model';

@Injectable({
  providedIn: 'root'
})
export class FacilityExportService {
  private _BASE_URL = environment.BusinessApi;
  private _LOCAL_API = environment.LocalJsonApi;
  private _LOCAL_EXPORTFILE_FILENAME = 'facilityexport.json';
  private _LOCAL_EXPORTACTIVITYFILE_FILENAME = 'facilityexportFileActivity.json';
  private _LOCAL_EXPORT_REQUEST = 'facilityRequestExport.json';
  private _NO_DATA = { data: [] };

  constructor(private http: HttpClient, private logger: LoggingMessageService, private authService: AuthService, private sessionService: StorageService) { }

    // Export Options
    public getGetFacilityExport(hospid: number): Observable<DataResult> {
      if (environment.UseBusinessServerForFacilityExportOptions) {
        return this.getBusinessFacilityExport(hospid);
      } else {
         return this.getLocalFacilityExport();
       }
    }

    
  private getLocalFacilityExport(): Observable<DataResult> {
    const uri = `${this._LOCAL_API}${this._LOCAL_EXPORTFILE_FILENAME}`;

    return this.http.get<DataResult>(
      uri, { observe: 'response' }
    )
      .pipe(
        map(response => {
          delay(5000);
          let result: DataResult;
          if (response instanceof HttpResponse) {
            if (response.status === 200) {
              result = new DataResult(response.ok, response.status, response.statusText, response.body);
            }
          }
          if (response instanceof HttpErrorResponse) {
            result = new DataResult(response.ok, response.status, response.statusText, this._NO_DATA);
          }
          return result.Data;
        }),
        catchError(error => {
          return this.handleError(`getLocal-SystemExport failed`, error);
        }),
        take(1)
      );
  }
  
    private getBusinessFacilityExport(hospid: number): Observable<DataResult> {
      const uri = `${this._BASE_URL}FacilityExport/GetFacilityExport`;
      const media = environment.GetFacilityExportMedia;
      return this.http
        .get<DataResult>(uri,
          {
            headers: { Accept: media },
            observe: 'response'
          }
        )
        .pipe(
          map(response => {
            let result: DataResult;
            if (response instanceof HttpResponse) {
              if (response.status === 200) {
                result = new DataResult(response.ok, response.status, response.statusText, response.body);
              }
            }
            if (response instanceof HttpErrorResponse) {
              result = new DataResult(response.ok, response.status, response.statusText, this._NO_DATA);
            }
  
            return result.Data;
          }),
          catchError(error => {
            return this.handleError(`getBusinessFacilityExport failed`, error);
          }),
          take(1)
        );
    }
  
    // Export Activity
    public getFacilityExportActivity(hospid: number): Observable<DataResult> {
      if (environment.UseBusinessServerForExportActivity) {
        return this.getBusinessFacilityExportActivity(hospid);
      } else {
        return this.getLocalFacilityExportActivity();
      }
    }
  
    private getLocalFacilityExportActivity(): Observable<DataResult> {
      const uri = `${this._LOCAL_API}${this._LOCAL_EXPORTACTIVITYFILE_FILENAME}`;
  
      return this.http.get<DataResult>(
        uri, { observe: 'response' }
      )
        .pipe(
          map(response => {
  
            delay(5000);
  
            let result: DataResult;
            if (response instanceof HttpResponse) {
              if (response.status === 200) {
                result = new DataResult(response.ok, response.status, response.statusText, response.body);
              }
            }
            if (response instanceof HttpErrorResponse) {
              result = new DataResult(response.ok, response.status, response.statusText, this._NO_DATA);
            }
  
            return result.Data;
          }),
          catchError(error => {
            return this.handleError(`getLocalFacilityExportActivity failed`, error);
          }),
          retry(3),
          take(1)
        );
    }
  
    private getBusinessFacilityExportActivity(hospid: number): Observable<DataResult> {
      const uri = `${this._BASE_URL}FacilityExport/GetFacilityExportLog?facilityID=${hospid}`;
      const media = environment.GetFacilityExportActivityMedia;
      return this.http
        .get<DataResult>(uri,
          {
            headers: { Accept: media },
            observe: 'response'
          }
        )
        .pipe(
          map(response => {
            let result: DataResult;
            if (response instanceof HttpResponse) {
              if (response.status === 200) {
                result = new DataResult(response.ok, response.status, response.statusText, response.body);
              }
            }
            if (response instanceof HttpErrorResponse) {
              result = new DataResult(response.ok, response.status, response.statusText, this._NO_DATA);
            }
  
            return result.Data;
          }),
          catchError(error => {
            return this.handleError(`getBusiness-FacilityExportActivity failed`, error);
          }),
          take(1)
        );
    }
  
    // Export File
    public getFacilityExportFile(hospid: number, fileid: number): Observable<FileResult> {
      if (environment.UseBusinessServerForExportActivity) {
        return this.getBusinessFacilityExportFile(hospid, fileid);
      } else {
        return this.getLocalFacilityExportFile();
      }
    }
  
    private getLocalFacilityExportFile(): Observable<FileResult> {
      const uri = `${this._LOCAL_API}${this._LOCAL_EXPORTFILE_FILENAME}`;
  
      return this.http.get<FileResult>(
        uri, { observe: 'response' }
      )
        .pipe(
          map(response => {
  
            delay(5000);
  
            let result: FileResult;
            if (response instanceof HttpResponse) {
              if (response.status === 200) {
                result = new FileResult(response.ok, response.status, response.statusText, response.body, '');
              }
            }
            if (response instanceof HttpErrorResponse) {
              result = new FileResult(response.ok, response.status, response.statusText, this._NO_DATA, '');
            }
  
            return result;
          }),
          catchError(error => {
            return this.handleError(`getLocalFacilityExportFile failed`, error);
          }),
          retry(3),
          take(1)
        );
    }
  
    private getFileNameFromResponseContentDisposition(res: any): string {
      let fileName = '';
      let contentDisposition = '';
      try {
        contentDisposition = res.headers.get('Content-Disposition') || '';
        const parts = contentDisposition.split('"');
        fileName = parts[1];
      } catch (error) {
         this.logger.error('facilityexport.service', 'getFileNameFromResponseContentDisposition', this.authService.SelectedSubscription, this.authService.AvailableSubscriptionsString, `can't get content disposition`, `Content-Disposition:${contentDisposition}`);
      }
      return fileName;
    }
  
    private getBusinessFacilityExportFile(hospid: number, fileid: number): Observable<FileResult> {
      const uri = `${this._BASE_URL}FacilityExport/GetFacilityExportFile?facilityID=${hospid}&recordID=${fileid}`;
      // this.logger.info('facilityexport.service', 'getBusinessFacilityExportFile', `Requesting download:${uri}`, '');
      const media = environment.GetFacilityExportFileMedia;
      return this.http
        .get<DataResult>(uri,
          {
            headers: { Accept: media },
            responseType: 'blob' as 'json',
            observe: 'response',
          }
        )
        .pipe(
          map(response => {
            let result: FileResult;
            if (response instanceof HttpResponse) {
              if (response.status === 200) {
                const fileName = this.getFileNameFromResponseContentDisposition(response);
                result = new FileResult(response.ok, response.status, response.statusText, response.body, fileName);
              }
            }
            if (response instanceof HttpErrorResponse) {
              result = new FileResult(response.ok, response.status, response.statusText, this._NO_DATA, '');
            }
  
            return result;
          }),
          catchError(error => {
            return this.handleError(`getBusinessFacilityExportFile failed`, error);
          }),
          take(1)
        );
    }
  
  // Generate Export File
  public generateFacilityExportFile(hospid: number, selectedExportID: number, selectedStartYear: number, selectedStartQuarter: number, selectedEndYear: number, selectedEndQuarter: number): Observable<DataResult> {
      if (environment.UseBusinessServerForExportActivity) {
        return this.generateBusinessFacilityExportFile(hospid, selectedExportID, selectedStartYear, selectedStartQuarter, selectedEndYear, selectedEndQuarter);
      } else {
        return this.generateLocalRequestFacilityExportFile();
    }
  }
  
  private generateLocalRequestFacilityExportFile(): Observable<DataResult> {
    const uri = `${this._LOCAL_API}${this._LOCAL_EXPORT_REQUEST}`;
  
    return this.http.get<FileResult>(
      uri, { observe: 'response' }
    )
      .pipe(
        map(response => {
  
          delay(5000);
  
          let result: DataResult;
          if (response instanceof HttpResponse) {
            if (response.status === 200) {
              result = new DataResult(response.ok, response.status, response.statusText, response.body);
            }
          }
          if (response instanceof HttpErrorResponse) {
            result = new DataResult(response.ok, response.status, response.statusText, this._NO_DATA);
          }
  
          return result;
        }),
        catchError(error => {
          return this.handleError(`generateLocalRequestFacilityExportFile failed`, error);
        }),
        retry(3),
        take(1)
      );
  }
  
  private generateBusinessFacilityExportFile(hospid: number, exportID: number, startYear: number, startQuarter: number, endYear: number, endQuarter: number): Observable<DataResult> {
    const uri = `${this._BASE_URL}FacilityExport/RequestExport?hospid=${hospid}&startYr=${startYear}&startQtr=${startQuarter}&endYr=${endYear}&endQtr=${endQuarter}&fileTypeID=${exportID}`;
    const media = environment.RequestFacilityExportFileMedia;
    return this.http
      .get<DataResult>(uri,
        {
          headers: { Accept: media },
          observe: 'response',
        }
      )
      .pipe(
        map(response => {
          let result: DataResult;
          if (response instanceof HttpResponse) {
            if (response.status === 200) {
              result = new DataResult(response.ok, response.status, response.statusText, response.body);
            }
          }
          if (response instanceof HttpErrorResponse) {
            result = new DataResult(response.ok, response.status, response.statusText, this._NO_DATA);
          }
  
          return result;
        }),
        catchError(error => {
          return this.handleError(`generateBusinessFacilityExportFile failed`, error);
        }),
        take(1)
      );
  }
  
  
    private handleError(operation: string, error: any) {
      let result: any;
       this.logger.error('facilityexport.service', 'handleError', this.authService.SelectedSubscription, this.authService.AvailableSubscriptionsString, `Operation: ${operation}, Resource`, error.message);
      if (error instanceof HttpErrorResponse) {
        const errorResponse = error as HttpErrorResponse;
        if (errorResponse && (errorResponse.status === 401)) {
          throw errorResponse;
        } else {
          const empty = new DataResult(false, 0, error.message, this._NO_DATA);
          result = empty;
        }
      }
      return of(result);
    }

    public getActionCss(status) {
      switch (status.toLowerCase()) {
        case 'in process':
          return 'invisible';
        case 'available':
          return 'visible';
        default:
          return 'invisible';
      }
    }
  

    public getStatusCss(status) {
      switch (status.toLowerCase()) {
        case 'in process':
          return 'orange';
        case 'available':
          return 'green';
        default:
          return 'red';
      }
    }
}
